import React, { memo, useCallback } from 'react';
import type {
	// eslint-disable-next-line jira/import/no-restricted-import
	RealtimeEvent,
	// eslint-disable-next-line jira/import/no-restricted-import
	RealtimeEventType,
} from '@atlassian/jira-realtime/src/common/types/events.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import type { IssueEventPayload } from '@atlassian/jira-realtime/src/common/types/payloads.tsx';
import type { PartialVersion } from '@atlassian/jira-software-version-backlog-panel-relay/src/common/types.tsx';
import type { IssueParent } from '../../model/issue-parent/types.tsx';
import { cardsParentSetOptimistic } from '../../state/actions/cards/parent/index.tsx';
import { parentClick } from '../../state/actions/cards/selection/index.tsx';
import { updateDetailView } from '../../state/actions/detail-view/index.tsx';
import { issueParentsCreateSet } from '../../state/actions/issue-parents/create/index.tsx';
import { updateVersion } from '../../state/actions/version/index.tsx';
import {
	useBacklogSelector,
	useBacklogDispatch,
	useBacklogActionCreator,
} from '../../state/index.tsx';
import { getEpicLinkFieldId } from '../../state/selectors/app/index.tsx';
import {
	getFirstNonEmptyCardListId,
	getLastNonEmptyCardListId,
	selectedCardsForVersionsPanelSelector,
} from '../../state/selectors/cards/index.tsx';
import { getLastFilteredIssueTypeId } from '../../state/selectors/filters/index.tsx';
import { useIsCMPBacklog } from '../../state/state-hooks/capabilities.tsx';
import { useOnIssueVersionsUpdated } from '../../state/state-hooks/use-on-issue-versions-updated.tsx';
import { useBatchOnReceive } from './utils.tsx';
import BacklogContent, { type Props as BacklogContentProps } from './view.tsx';

export type Props = Omit<
	BacklogContentProps,
	| 'selectedCards'
	| 'selectedCardTypeId'
	| 'onCardDetailClick'
	| 'onEpicCreate'
	| 'onEpicAssign'
	| 'onVersionAltered'
	| 'onIssueVersionsUpdated'
	| 'onReceive'
	| 'onReceiveEvent'
>;

const BacklogContentContainerInner = (props: Props) => {
	const dispatch = useBacklogDispatch();
	const selectedCards = useBacklogSelector(selectedCardsForVersionsPanelSelector);
	const lastFilteredIssueTypeId = useBacklogSelector(getLastFilteredIssueTypeId);
	const isCompanyManaged = useIsCMPBacklog();
	const epicLinkFieldId = useBacklogSelector(getEpicLinkFieldId);
	const firstNonEmptyCardListId = useBacklogSelector(getFirstNonEmptyCardListId);
	const lastNonEmptyCardListId = useBacklogSelector(getLastNonEmptyCardListId);

	const pushEvent = useBatchOnReceive();

	const onCardDetailClick = useBacklogActionCreator(parentClick);

	const onEpicCreate = useBacklogActionCreator((issueParents: IssueParent[]) =>
		issueParentsCreateSet({ issueParents }),
	);

	const onVersionAltered = useCallback(
		(version: PartialVersion) => {
			if (isCompanyManaged) {
				dispatch(
					updateVersion({
						id: version.versionId,
						name: version.name,
						projectId: Number(version.projectId),
					}),
				);
			}
			dispatch(updateDetailView());
		},
		[dispatch, isCompanyManaged],
	);

	const onIssueVersionsUpdated = useOnIssueVersionsUpdated();

	const onEpicAssign = useBacklogActionCreator(
		(cards: { id: number }[], parentId: number | null, requestPromise: Promise<undefined>) =>
			cardsParentSetOptimistic({ cards, parentId, requestPromise }),
	);

	// TODO: remove on 'jira_replace_provider_and_client_with_realtime' feature gate clean up
	const onReceive = useCallback(
		(eventType: RealtimeEventType, payload: IssueEventPayload) => {
			const event: RealtimeEvent = {
				type: eventType,
				payload,
			};
			pushEvent(event);
		},
		[pushEvent],
	);

	return (
		<BacklogContent
			{...props}
			selectedCards={selectedCards}
			selectedCardTypeId={lastFilteredIssueTypeId}
			onCardDetailClick={onCardDetailClick}
			onEpicCreate={onEpicCreate}
			onEpicAssign={onEpicAssign}
			onIssueVersionsUpdated={onIssueVersionsUpdated}
			onVersionAltered={onVersionAltered}
			onReceive={onReceive}
			onReceiveEvent={pushEvent}
			isCompanyManaged={isCompanyManaged}
			epicLinkFieldId={epicLinkFieldId}
			firstNonEmptyCardListId={firstNonEmptyCardListId}
			lastNonEmptyCardListId={lastNonEmptyCardListId}
			issueHierarchyLevel={props.issueHierarchyLevel}
		/>
	);
};

const BacklogContentContainer = memo(BacklogContentContainerInner);
export default BacklogContentContainer;
